<div class="shadow-md bg-white w-full flex flex-col max-h-full">
  <div class="flex flex-row justify-between items-center p-8 bg-primary-600 text-contrast overflow-hidden rounded-t-md">
    <h2 class="m-0 font-bold">{{ data.event.calendar.designation | i18n }}</h2>
    @if ((displayType | async) !== 'loading') {
      <button (click)="close()">
        <ng-icon name="heroXMark" class="h-5 w-5 flex-shrink-0" size="24px"></ng-icon>
      </button>
    }
  </div>

  <div class="p-8 overflow-auto">
    <div class="flex-flex-col">
      <div class="block text-sm leading-6 text-gray-900 font-bold">{{ { key: 'date' } | i18n }}</div>
      <div class="text-sm mt-2">
        {{ { date: data.event.from, format: 'EEEE, dd.LL.yyyy' } | luxon }}
        @if (!data.option.allowDurationChange) {
          {{ { date: data.event.from, format: 'HH:mm' } | luxon }} - {{ { date: data.event.until, format: 'HH:mm' } | luxon }}
        }
      </div>
    </div>

    @switch (displayType | async) {
      @case ('success') {
        <div class="flex flex-row items-center my-12">
          <ng-icon name="heroCheckCircle" size="32px" class="mr-4 text-green-700"></ng-icon>
          <div class="flex flex-col">
            <h3 class="mb-2">{{ { key: 'bookingOverlaySuccessTitle' } | i18n }}</h3>
            <p class="text-sm">{{ { key: 'bookingOverlaySuccessEmail' } | i18n }}</p>
          </div>
        </div>
        <button
          class="w-full items-center justify-center border border-transparent py-2 px-4 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-50 bg-primary-600 hover:bg-primary-700 focus:ring-primary-500"
          (click)="close(true)"
        >
          {{ { key: 'bookingOverlayClose' } | i18n }}
        </button>
      }
      @case ('error') {
        <div class="flex flex-row items-center my-12">
          <ng-icon name="heroXCircle" size="32px" class="mr-4 text-red-600"></ng-icon>
          <h3 class="mb-0">{{ { key: 'bookingOverlayErrorCreatingBooking' } | i18n }}</h3>
        </div>
        <button
          class="w-full items-center justify-center border border-transparent py-2 px-4 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-50 bg-primary-600 hover:bg-primary-700 focus:ring-primary-500"
          (click)="close()"
        >
          {{ { key: 'bookingOverlayClose' } | i18n }}
        </button>
      }
      @case ('redirectToPayment') {
        <div class="flex flex-row items-center my-12">
          <ng-icon name="heroArrowUpRight" size="32px" class="mr-4 text-gray-500"></ng-icon>
          <h3 class="mb-0">{{ { key: 'bookingOverlayRedirectPayment' } | i18n }}</h3>
        </div>
      }
      @case ('loading') {
        <div class="flex flex-row items-center my-12">
          <app-loading [classes]="['h-6', 'w-6', 'mr-4']" [noWhitespace]="true" [hideText]="true"></app-loading>
          <h3 class="mb-0">{{ { key: 'bookingOverlayPleaseWait' } | i18n }}</h3>
        </div>
      }
      @default {
        @if ((data.option.usageTypes?.length || 0) > 0) {
          <fieldset class="mt-8">
            <div class="divide-y divide-gray-200 border-b border-t border-gray-200">
              @for (type of data.option.usageTypes; track type._id) {
                <div class="relative flex items-start py-4">
                  <div class="mr-3 flex h-6 items-center">
                    <input
                      [id]="type._id"
                      [name]="type._id"
                      type="radio"
                      [checked]="type._id === (usageType | async)"
                      (change)="usageType.next(type._id)"
                      class="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-600"
                    />
                  </div>
                  <div class="min-w-0 flex-1 text-sm leading-6">
                    <label [for]="type._id" class="select-none font-medium text-gray-900">{{ type.text }}</label>
                  </div>
                </div>
              }
            </div>
          </fieldset>
        }

        @if (data.option.allowDurationChange) {
          <div class="mt-8">
            <label for="duration-select" class="block text-sm leading-6 text-gray-900 font-bold">{{
              { key: 'bookingDuration' } | i18n
            }}</label>
            <select
              id="duration-select"
              name="duration-select"
              class="mt-2 block w-full border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-primary-600 sm:text-sm sm:leading-6"
              (change)="selectedDuration.next($any($event).target.value)"
            >
              @for (item of durationRows | async; track item.text) {
                <option [selected]="item._id === (selectedDuration | async)" [value]="item._id">{{ item.text | i18n }}</option>
              }
            </select>
          </div>
        }

        <div class="mt-8">
          <label for="payment-method-select" class="block text-sm leading-6 text-gray-900 font-bold">{{
            { key: 'paymentMethod' } | i18n
          }}</label>
          <select
            id="payment-method-select"
            name="payment-method-select"
            class="mt-2 block w-full border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-primary-600 sm:text-sm sm:leading-6"
            [ngModel]="selectedPaymentMethod | async"
            (ngModelChange)="selectedPaymentMethod.next($event)"
          >
            @for (item of paymentMethods | async; track item._id) {
              <option [value]="item._id">{{ item.displayValue | i18n }}</option>
            }
          </select>
        </div>

        @if (meta | async; as m) {
          @if (m.maxCompanionCount > 0) {
            <div class="mt-8">
              @if (selectedCompanions | async; as selected) {
                <div for="payment-method-select" class="block text-sm leading-6 text-gray-900 font-bold">
                  {{ { key: 'companionCount', values: [selected.length, m.maxCompanionCount] } | i18n }}
                </div>
                @if ((selected?.length || 0) > 0) {
                  <div class="py-2 gap-2 flex flex-col">
                    @for (companion of selectedCompanions | async; track companion._id) {
                      <div class="text-sm w-full flex flex-row justify-between">
                        <div>{{ companion.displayValue }}</div>
                        <button (click)="removeCompanion(companion)">
                          <ng-icon name="heroXMark" class="h-4 w-4 flex-shrink-0" size="18px"></ng-icon>
                        </button>
                      </div>
                    }
                  </div>
                }

                @if (selected.length < m.maxCompanionCount) {
                  <app-combobox
                    [values]="companions | async"
                    placeholder="{{ { key: 'addCompanion' } | i18n }}"
                    [filterOnly]="true"
                    [allowNewEntry]="true"
                    (selected)="addCompanion($event)"
                    (createNewEntry)="createCompanion()"
                  ></app-combobox>

                  <div class="mt-4">
                    <button class="text-primary-600 text-sm" (click)="createCompanion()">{{ { key: 'recordNewCompanion' } | i18n }}</button>
                  </div>
                }
              }
            </div>
          }
        }

        @if (meta | async; as m) {
          @if ((m.usageTypes?.length || 0) > 0) {
            <div class="mt-8">
              <div for="payment-method-select" class="block text-sm leading-6 text-gray-900 font-bold">
                {{ { key: 'allowedUsageTypes' } | i18n }}
              </div>
              <ul class="py-2 gap-2 list-disc">
                @for (usageType of m.usageTypes; track usageType._id) {
                  <li class="text-sm w-full ml-4">
                    {{ usageType.displayValue | i18n }}
                  </li>
                }
              </ul>
            </div>
          }
        }

        <div class="mt-8 text-sm h-24 flex flex-col justify-center">
          @if (isCalculatingPrice | async) {
            <app-loading [noWhitespace]="true"></app-loading>
          } @else {
            @if (errorOnPriceCalculation()) {
              <div class="flex flex-row justify-center items-center w-full gap-4">
                <ng-icon name="heroXCircle" size="24px" class="text-red-600"></ng-icon>
                <div class="text-red-600">{{ { key: 'priceCalculationError' } | i18n }}</div>
              </div>
            } @else {
              @if ((selectedPaymentMethod | async) === '#credit') {
                <div class="flex flex-row justify-between">
                  <div>{{ { key: 'currentCredit' } | i18n }}</div>
                  <div>{{ (meta | async)?.creditBalance | number: '1.2-2' }}</div>
                </div>
                <div class="mt-2 flex flex-row justify-between">
                  <div class="font-bold">{{ { key: 'requiredCredit' } | i18n }}</div>
                  <div class="font-bold">{{ (price | async)?.credits | number: '1.2-2' }}</div>
                </div>
                <div
                  class="mt-2 flex flex-row justify-between"
                  [ngClass]="{ 'text-red-600': ((meta | async)?.creditBalance || 0) - ((price | async)?.credits || 0) < 0 }"
                >
                  <div>{{ { key: 'newCredit' } | i18n }}</div>
                  <div>{{ ((meta | async)?.creditBalance || 0) - ((price | async)?.credits || 0) | number: '1.2-2' }}</div>
                </div>
                <div class="mt-4">
                  <button class="text-primary-600" (click)="openCreditTopUpOverlay()">{{ { key: 'topUpCredit' } | i18n }}</button>
                </div>
              } @else {
                <div class="flex flex-row justify-between">
                  <div class="font-bold">{{ { key: 'priceInclVat' } | i18n }}</div>
                  <div class="font-bold">{{ (price | async)?.priceInclVat | bbitFormatPrice }}</div>
                </div>
              }
            }
          }
        </div>

        @if (legalFiles | async; as files) {
          @if (files.length > 0) {
            <p class="mt-8 mb-4 text-sm">{{ { key: 'acceptConditions' } | i18n }}</p>
            <div class="flex flex-col gap-2">
              @for (file of files; track file._id) {
                <div class="relative flex flex-row items-center justify-between">
                  <div class="relative flex flex-row items-center">
                    <div class="flex h-6 items-center">
                      <input
                        id="legal-file-{{ file._id }}"
                        type="checkbox"
                        class="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                        [ngModel]="acceptedLegalFiles().includes(file._id)"
                        (ngModelChange)="toggleAcceptedLegalFile(file._id)"
                      />
                    </div>
                    <div class="ml-3 text-sm leading-6">
                      <label for="legal-file-{{ file._id }}" class="font-medium text-gray-900">{{ file.designation | i18n }}</label>
                    </div>
                  </div>
                  <a
                    [href]="getDownloadUrl(file)"
                    target="_blank"
                    class="ml-4 text-sm text-gray-500 hover:text-primary-600 flex flex-row items-center space-x-2"
                  >
                    <ng-icon name="heroArrowDownLeft"></ng-icon>{{ { key: 'download' } | i18n }}
                  </a>
                </div>
              }
            </div>
          }
        }

        <button
          class="mt-8 w-full items-center justify-center border border-transparent py-2 px-4 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-50"
          [ngClass]="{
            'bg-primary-600': canBeBooked | async,
            'hover:bg-primary-700': canBeBooked | async,
            'focus:ring-primary-500': canBeBooked | async,
            'focus:ring-orange-500': !(canBeBooked | async),
            'bg-gray-400': !(canBeBooked | async),
          }"
          (click)="book()"
        >
          {{ { key: 'book' } | i18n }}
        </button>
        <button
          class="mt-4 w-full items-center justify-center text-primary-600 hover:text-primary-700 border border-transparent py-2 px-4 text-sm font-medium focus:outline-none"
          (click)="close()"
        >
          {{ { key: 'abort' } | i18n }}
        </button>
      }
    }
  </div>
</div>
